import { fetchWithTimeout } from "./http";
import { readApiResponse } from "./apiResponse";
import { dataSourceGetUrl, dataSourceRequestInit, isDataSourceConfigured, type UserDataSource } from "./dataSource";
import { GROUP_ORDER, type CompareGroup, type CoordItem } from "../types";
import type { MixCoordinate, MixCoordinateItems, MixCoordinateRegistration, MixScope } from "../features/mixes/types";

type RawMixCoordinate = {
  id?: unknown;
  name?: unknown;
  brand?: unknown;
  items?: unknown;
  scope?: unknown;
  createdAt?: unknown;
  created_at?: unknown;
};

function parseJsonValue(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function namedValue(value: unknown): string {
  const parsed = parseJsonValue(value);
  if (typeof parsed === "string") return parsed.trim();
  if (parsed && typeof parsed === "object" && "name" in parsed) return String(parsed.name ?? "").trim();
  return "";
}

function normalizeItems(value: unknown): MixCoordinateItems {
  const parsed = parseJsonValue(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
  const source = parsed as Record<string, unknown>;
  const items: MixCoordinateItems = {};
  for (const group of GROUP_ORDER) {
    const ids = source[group];
    if (!Array.isArray(ids)) continue;
    const normalized = ids.map(String).map((id) => id.trim()).filter(Boolean);
    if (normalized.length) items[group] = normalized;
  }
  return items;
}

export function parseMixCoordinate(value: unknown): MixCoordinate | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value as RawMixCoordinate;
  const id = String(raw.id ?? "").trim();
  const name = namedValue(raw.name);
  if (!id || !name) return null;
  const scope: MixScope = raw.scope === "global" ? "global" : "local";
  const createdAtValue = raw.createdAt ?? raw.created_at;
  return {
    id,
    name,
    brand: namedValue(raw.brand),
    items: normalizeItems(raw.items),
    scope,
    ...(createdAtValue ? { createdAt: String(createdAtValue) } : {}),
  };
}

export function groupMixItemIds(items: readonly CoordItem[]): MixCoordinateItems {
  const grouped: MixCoordinateItems = {};
  for (const group of GROUP_ORDER) {
    const ids = items.filter((item) => item.term === group).map((item) => item.id.trim()).filter(Boolean);
    if (ids.length) grouped[group as CompareGroup] = ids;
  }
  return grouped;
}

async function readPayload(response: Response): Promise<unknown> {
  const payload: unknown = await response.json().catch(() => null);
  if (payload && typeof payload === "object" && "error" in payload && payload.error) {
    throw new Error(String(payload.error));
  }
  return payload;
}

async function fetchMixCoordinateSource(source: UserDataSource): Promise<MixCoordinate[]> {
  const response = await fetchWithTimeout(dataSourceGetUrl(source, "mix-GET"), dataSourceRequestInit(source));
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const payload = await readPayload(response);
  if (!Array.isArray(payload)) throw new Error("mix-GET: 配列形式ではありません。");
  return payload.map(parseMixCoordinate).filter((item): item is MixCoordinate => item !== null);
}

export async function fetchMixCoordinates(source: UserDataSource): Promise<MixCoordinate[]> {
  if (source.kind === "spreadsheet") return fetchMixCoordinateSource(source);
  if (!isDataSourceConfigured(source)) return [];
  const [shared, personal] = await Promise.all([
    fetchMixCoordinateSource({ kind: "spreadsheet", sheetId: "" }),
    fetchMixCoordinateSource(source),
  ]);
  return [...new Map([...shared, ...personal].map((mix) => [mix.id, mix])).values()];
}

export async function postMixCoordinate(
  source: UserDataSource,
  registration: MixCoordinateRegistration,
  options: { id?: string } = {},
): Promise<MixCoordinate> {
  if (!isDataSourceConfigured(source)) throw new Error("保存先が設定されていません。");
  const name = registration.name.trim();
  const brand = registration.brand.trim();
  const items = groupMixItemIds(registration.items);
  if (!name) throw new Error("ミックスコーデ名を入力してください。");
  if (!brand) throw new Error("ブランドを選択してください。");
  if (!Object.keys(items).length) throw new Error("登録するコーデパーツがありません。");
  const id = options.id ?? crypto.randomUUID();
  const response = await fetchWithTimeout(dataSourceGetUrl(source, "mix-local-POST"), dataSourceRequestInit(source, {
      method: "mix-local-POST",
      id,
      name: JSON.stringify({ name }),
      items: JSON.stringify(items),
      brand: JSON.stringify({ name: brand }),
  }));
  await readApiResponse(response);
  return { id, name, brand, items, scope: "local" };
}
